import React, { useEffect, useState, ReactNode } from 'react';
import { Navigate, Outlet } from 'react-router-dom';
import { getCurrentSession, validateSession } from '../lib/db';
import logger from '../utils/logger';

interface ProtectedRouteProps {
  children?: ReactNode;
  requireAdmin?: boolean;
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children, requireAdmin = false }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let mounted = true;

    const checkAuth = async () => {
      try {
        const session = getCurrentSession();
        logger.info('Checking authentication', {
          component: 'ProtectedRoute',
          action: 'checkAuth',
          hasSession: !!session
        });

        if (!session) {
          setError('No session found');
          setIsLoading(false);
          return;
        }

        // Validate the session
        const validatedSession = await validateSession();
        if (mounted) {
          if (validatedSession?.user) {
            setIsAuthenticated(true);
            setIsAdmin(validatedSession.user.role === 'admin');
            logger.info('Session validated successfully', {
              component: 'ProtectedRoute',
              action: 'validateSession',
              isAdmin: validatedSession.user.role === 'admin'
            });
          } else {
            setError('Session validation failed');
            logger.warn('Session validation failed', {
              component: 'ProtectedRoute',
              action: 'validateSession'
            });
          }
          setIsLoading(false);
        }
      } catch (error) {
        if (mounted) {
          const errorMessage = error instanceof Error ? error.message : 'Authentication check failed';
          setError(errorMessage);
          setIsLoading(false);
          logger.error('Authentication check failed', {
            component: 'ProtectedRoute',
            action: 'checkAuth',
            error: errorMessage
          });
        }
      }
    };

    checkAuth();

    return () => {
      mounted = false;
    };
  }, []);

  if (isLoading) {
    return (
      <div style={{ 
        display: 'flex', 
        flexDirection: 'column',
        alignItems: 'center', 
        justifyContent: 'center', 
        height: '100vh',
        background: 'var(--bg)'
      }}>
        <div style={{
          width: 40,
          height: 40,
          border: '3px solid var(--line)',
          borderTopColor: 'var(--accent)',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }} />
        <p style={{ 
          marginTop: 16, 
          color: 'var(--ink)',
          fontFamily: 'var(--font-body)',
          fontSize: 14
        }}>
          Verifying authentication...
        </p>
        <style>{`
          @keyframes spin {
            to { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  if (!isAuthenticated) {
    logger.info('Redirecting to login', {
      component: 'ProtectedRoute',
      action: 'redirect',
      reason: error || 'Not authenticated'
    });
    return <Navigate to="/login" replace />;
  }

  if (requireAdmin && !isAdmin) {
    logger.warn('Non-admin user attempted to access admin route', {
      component: 'ProtectedRoute',
      action: 'redirect',
      reason: 'Not admin'
    });
    return <Navigate to="/" replace />;
  }

  return <>{children || <Outlet />}</>;
}; 
